import { el, triggerDownload, copyToClipboard } from "./dom";
import { iconPlus, iconTrash, iconLock, iconShield, iconCopy } from "./icons";
import { renderDropzone, formatBytes } from "./dropzone";
import { showToast } from "./toast";
import type { CryptoWorkerClient } from "../lib/workerClient";
import { uploadBlob, type ApiConfig } from "../lib/api";
import { apiTokenState } from "../lib/apiToken";
import { humanizeGeneric } from "../lib/humanizeError";
import { bytesToHex, hexToBytes } from "../lib/bytes";
import { renderSnippetTabs } from "./codeSnippets";

const ENCRYPT_FILE_SNIPPETS = {
  sdk: `import { encryptFile, encryptFileForRecipient } from "secure-crypt-sdk";

// One or more independent passwords, any one unlocks the file:
const container = encryptFile(fileStream, { alice: alicePw, bob: bobPw });

// Or seal it to a single recipient's public key -- a one-time password
// is generated for you and sealed to their key, you never handle it:
const { chunks, envelope } = await encryptFileForRecipient(fileStream, recipientPublicKey);`,
  cli: `# Password slots:
python -m cli.main encrypt-file --in report.pdf --out report.enc \\
  --password alice --password bob

# Or for a single recipient's public key (fully scriptable, no prompt):
python -m cli.main share-file --in report.pdf --pubkey recipient_public.key \\
  --out report.enc --envelope-out report.envelope`,
  http: `# Optional: upload the resulting container to the relay for the
# recipient to fetch later -- see the Service API page.
curl -X POST http://localhost:8000/blobs -H "X-Api-Key: <key>" --data-binary @report.enc`,
};

interface SlotRow {
  row: HTMLElement;
  label: HTMLInputElement;
  password: HTMLInputElement;
}

export function renderEncryptFilePanel(worker: CryptoWorkerClient, apiConfig: ApiConfig, requestToken: () => Promise<void>): HTMLElement {
  const dz = renderDropzone({
    id: "enc-file-input",
    emptyTitle: "No file chosen yet",
    emptyMeta: "Choose a file to seal it with one or more passwords.",
    onFileChange: (f) => {
      if (f) dz.setDisplay(f.name, `${formatBytes(f.size)} · not yet encrypted`);
      else dz.reset();
    },
  });
  const fileInput = dz.fileInput;

  // Two mutually exclusive ways to seal a file: passwords, or a single
  // recipient's public key. Only the active mode's fields get used; the
  // other's stay hidden but not cleared, so toggling back doesn't lose input.
  let mode: "passwords" | "recipient" = "passwords";
  const passwordsModeBtn = el("button", { type: "button", class: "segmented-btn active" }, ["One or more passwords"]);
  const recipientModeBtn = el("button", { type: "button", class: "segmented-btn" }, ["A recipient's public key"]);
  const modeToggle = el("div", { class: "segmented" }, [passwordsModeBtn, recipientModeBtn]);

  const slotsContainer = el("div", { class: "tumblers", id: "enc-file-slots" }, []);
  const addSlotBtn = el("button", { type: "button", class: "add-way" }, [iconPlus(), "Add another way to unlock"]);
  const passwordsFields = el("div", {}, [
    el("p", { class: "section-label" }, ["Ways to unlock this file"]),
    el("p", { class: "section-sub" }, [
      "Anyone with one of these passwords can open the file on their own. Removing a way doesn't affect the others.",
    ]),
    slotsContainer,
    addSlotBtn,
  ]);

  const recipientPubkeyInput = el("input", {
    type: "text",
    placeholder: "Recipient's public key (hex)",
    id: "enc-file-recipient-pubkey",
    class: "mono-field",
  });
  const recipientFields = el("div", {}, [
    el("p", { class: "section-label" }, ["Recipient's public key"]),
    el("p", { class: "section-sub" }, [
      "A random one-time password is generated for you and sealed to this key -- you never see or handle it yourself. ",
      "Both the encrypted file and the small \"envelope\" this produces must reach the recipient; ",
      "only they, with the matching private key, can open it.",
    ]),
    recipientPubkeyInput,
  ]);
  recipientFields.style.display = "none";

  function setMode(next: "passwords" | "recipient") {
    mode = next;
    passwordsModeBtn.classList.toggle("active", next === "passwords");
    recipientModeBtn.classList.toggle("active", next === "recipient");
    passwordsFields.style.display = next === "passwords" ? "" : "none";
    recipientFields.style.display = next === "recipient" ? "" : "none";
  }
  passwordsModeBtn.addEventListener("click", () => setMode("passwords"));
  recipientModeBtn.addEventListener("click", () => setMode("recipient"));

  // Persistent (not toast) results container - a blob_id/envelope is data
  // the user needs to copy later, so it stays on screen after the success
  // toast fades.
  const resultCard = el("div", {}, []);
  const progress = el("progress", { max: "100", value: "0" }, []);
  progress.style.display = "none";

  const slots: SlotRow[] = [];
  function removeSlotRow(entry: SlotRow) {
    if (slots.length <= 1) return; // always keep at least one way to unlock
    slots.splice(slots.indexOf(entry), 1);
    entry.row.remove();
  }

  function addSlotRow() {
    const label = el("input", { type: "text", placeholder: "Who's this for? e.g. \"Dr. Patel\"" }) as HTMLInputElement;
    const password = el("input", {
      type: "password",
      placeholder: "Their password",
      autocomplete: "new-password",
    }) as HTMLInputElement;

    const removeBtn = el("button", { type: "button", class: "icon-btn danger", "aria-label": "Remove this way to unlock" }, [
      iconTrash(),
    ]);
    const entry: SlotRow = {
      row: el("div", { class: "slot-row tumbler" }, [
        el("div", { class: "cylinder" }, [el("span", { class: "dot" }, [])]),
        el("div", { class: "tumbler-fields" }, [label, password]),
        el("div", { class: "tumbler-actions" }, [removeBtn]),
      ]),
      label,
      password,
    };
    removeBtn.addEventListener("click", () => removeSlotRow(entry));
    slots.push(entry);
    slotsContainer.append(entry.row);
  }
  addSlotRow();

  addSlotBtn.addEventListener("click", () => addSlotRow());

  const encryptBtn = el("button", { type: "button", class: "primary-btn" }, [iconLock(), "Encrypt file"]);

  function buildResultActions(file: File, blob: Blob, envelope: Uint8Array | null) {
    resultCard.innerHTML = "";
    const downloadBtn = el("button", { type: "button", class: "secondary-btn" }, ["Download .enc file"]);
    downloadBtn.addEventListener("click", () => triggerDownload(`${file.name}.enc`, blob));
    const actionsRow = el("div", { class: "actions" }, [downloadBtn]);

    if (envelope) {
      // Neither piece is sent anywhere by this app - both have to reach
      // the recipient through whatever channel the sender already uses.
      const envelopeHex = bytesToHex(envelope);
      const envelopeField = el("code", { class: "mono-field", id: "enc-file-envelope-result", title: envelopeHex }, [
        `${envelopeHex.slice(0, 16)}…${envelopeHex.slice(-16)}`,
      ]);
      const copyEnvelopeBtn = el("button", { type: "button", class: "icon-btn", "aria-label": "Copy envelope" }, [
        iconCopy(),
      ]);
      copyEnvelopeBtn.addEventListener("click", async () => {
        const ok = await copyToClipboard(envelopeHex);
        showToast(ok ? "Envelope copied." : "Couldn't copy - select and copy it manually.", ok ? "success" : "error");
      });
      const downloadEnvelopeBtn = el("button", { type: "button", class: "secondary-btn" }, ["Download envelope"]);
      downloadEnvelopeBtn.addEventListener("click", () =>
        triggerDownload(`${file.name}.envelope`, new Blob([envelope as Uint8Array<ArrayBuffer>])),
      );
      resultCard.append(
        el("p", { class: "field-label" }, ["Envelope (the recipient needs this too, alongside the .enc file)"]),
        el("div", { class: "actions" }, [envelopeField, copyEnvelopeBtn, downloadEnvelopeBtn]),
      );
    }

    const uploadBtn = el("button", { type: "button", class: "secondary-btn" }, ["Upload to service"]);
    uploadBtn.addEventListener("click", async () => {
      // Needs an API token - request one automatically if we don't have one yet.
      if (!apiTokenState.token) {
        await requestToken();
        if (!apiTokenState.token) return; // requestToken already surfaced the error
      }
      const token = apiTokenState.token;
      try {
        const result = await uploadBlob(apiConfig, blob, token);
        showToast("Uploaded to the service.", "success");
        const blobIdField = el("code", { class: "mono-field", id: "enc-file-blobid-result" }, [result.blob_id]);
        const copyBlobIdBtn = el("button", { type: "button", class: "icon-btn", "aria-label": "Copy blob_id" }, [
          iconCopy(),
        ]);
        copyBlobIdBtn.addEventListener("click", async () => {
          const ok = await copyToClipboard(result.blob_id);
          showToast(ok ? "blob_id copied." : "Couldn't copy - select and copy it manually.", ok ? "success" : "error");
        });
        resultCard.append(
          el("p", { class: "field-label" }, ["blob_id (needed to fetch this file back later)"]),
          el("div", { class: "actions" }, [blobIdField, copyBlobIdBtn]),
        );
      } catch (e) {
        showToast(humanizeGeneric(e, "Uploading to the service didn't work."), "error");
      }
    });
    actionsRow.append(uploadBtn);
    resultCard.append(actionsRow);
  }

  encryptBtn.addEventListener("click", async () => {
    const file = fileInput.files?.[0];
    if (!file) {
      showToast("Choose a file first.", "error");
      return;
    }

    progress.style.display = "";
    progress.value = 0;
    try {
      if (mode === "recipient") {
        let recipientPublicKey: Uint8Array;
        try {
          recipientPublicKey = hexToBytes(recipientPubkeyInput.value);
        } catch {
          showToast("Enter the recipient's public key as hex.", "error");
          return;
        }
        const { blob, envelope } = await worker.encryptFileForRecipient(file, recipientPublicKey, (processed, total) => {
          progress.value = total > 0 ? Math.round((processed / total) * 100) : 0;
        });
        showToast(`Sealed for that recipient. The encrypted file is ${formatBytes(blob.size)}.`, "success");
        buildResultActions(file, blob, envelope);
      } else {
        const passwords: Record<string, Uint8Array> = {};
        for (const s of slots) {
          if (!s.label.value) continue;
          passwords[s.label.value] = new TextEncoder().encode(s.password.value);
          s.password.value = "";
        }
        if (Object.keys(passwords).length === 0) {
          showToast("Give at least one way to unlock this file a label and a password.", "error");
          return;
        }
        const { blob } = await worker.encryptFile(file, passwords, (processed, total) => {
          progress.value = total > 0 ? Math.round((processed / total) * 100) : 0;
        });
        showToast(`Sealed. The encrypted file is ${formatBytes(blob.size)}.`, "success");
        buildResultActions(file, blob, null);
      }
    } catch (e) {
      showToast(humanizeGeneric(e, "Encrypting that file didn't work."), "error");
    } finally {
      progress.style.display = "none";
    }
  });

  return el("section", {}, [
    el("p", { class: "eyebrow" }, ["Files · Encrypt"]),
    el("h2", {}, ["Seal a file with as many keys as you need"]),
    el("p", { class: "lede" }, [
      "Encrypt once, then choose who can open it. Each person gets their own password - add or remove a way in without ever touching the others.",
    ]),
    el("p", { class: "section-label" }, ["Integration"]),
    renderSnippetTabs(ENCRYPT_FILE_SNIPPETS),
    el("p", { class: "section-label" }, ["Try it live"]),
    el("div", { class: "card" }, [
      dz.element,
      modeToggle,
      passwordsFields,
      recipientFields,
      el("div", { class: "divider" }, []),
      el("div", { class: "actions-row" }, [
        encryptBtn,
        el("div", { class: "trust-note" }, [
          iconShield(),
          el("span", {}, [
            "Encryption runs entirely in your browser. We never see the file, a password, or your key.",
          ]),
        ]),
      ]),
      progress,
      resultCard,
    ]),
  ]);
}
